import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import PrelitXLogo from '@/components/PrelitXLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If an account exists with that email, a reset link has been sent');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Still show success for security
      setSent(true);
      toast.success('If an account exists with that email, a reset link has been sent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-2xl border-0 glass-card">
        <CardHeader className="text-center space-y-4 pb-8">
          <div className="mx-auto mb-2" style={{ marginTop: '24px' }}>
            <PrelitXLogo size="large" className="justify-center" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold" style={{ color: '#245451' }}>
              Reset Password
            </CardTitle>
            <CardDescription className="mt-2" style={{ color: '#64748B' }}>
              {sent 
                ? "Check your email for the reset link"
                : "Enter your email to receive a password reset link"
              }
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-6">
              <div className="text-center p-6 rounded-lg" style={{ backgroundColor: 'rgba(245, 247, 250, 0.8)' }}>
                <p className="text-sm" style={{ color: '#64748B' }}>
                  We've sent a password reset link to <strong style={{ color: '#245451' }}>{email}</strong>. 
                  Please check your inbox and follow the instructions.
                </p>
              </div>
              <Link to="/login">
                <Button
                  className="w-full h-11 font-medium"
                  style={{ backgroundColor: '#245451' }}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" style={{ color: '#1A1A1A', fontWeight: 500 }}>Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="attorney@lawfirm.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  style={{ borderColor: '#E2E8F0', backgroundColor: '#FFFFFF' }}
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 font-medium"
                style={{ backgroundColor: '#245451' }}
                disabled={loading}
                data-testid="forgot-password-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}

          {!sent && (
            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm font-medium inline-flex items-center"
                style={{ color: '#406962' }}
              >
                <ArrowLeft className="mr-1 h-3 w-3" />
                Back to Login
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
